import { useMutation } from '@apollo/client';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Stack } from '@mui/material';
import {
  Mutation,
  MutationUpdateAccessGrantArgs,
  WeekScheduleDay,
  WeekScheduleDayInput
} from 'graphql/generated/graphqlTypes';
import { UPDATE_ACCESS_GRANT } from 'graphql/v2/mutations';
import { GET_ALL_VISITORS_GROUPS } from 'graphql/v2/queries';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';
import { EditScheduleFormValues, buildWeekSchedule, getFormDefaults } from '../../Details/components/editScheduleUtils';
import WeekScheduleFields from '../../components/WeekScheduleFields';

export const CREATE_GROUP_STEP_3_FORM_ID = 'create-group-step-3';
const STEP_INDEX = 2;

interface Props {
  grantId: string | null;
  weekDays: WeekScheduleDay[];
}

const GroupScheduleStep: React.FC<Props> = ({ grantId, weekDays }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToNext, goToPrev, setFooterContent, steps } = useWizardStepContext();

  const [updateAccessGrantMutation, { loading }] = useMutation<
    Pick<Mutation, 'updateAccessGrant'>,
    MutationUpdateAccessGrantArgs
  >(UPDATE_ACCESS_GRANT, {
    refetchQueries: [GET_ALL_VISITORS_GROUPS],
    awaitRefetchQueries: true,
    onError: () => showSnackbar('Error while updating schedule', { severity: 'error' })
  });

  const { handleSubmit, getValues, control, trigger, reset } = useForm<EditScheduleFormValues>({
    mode: 'onTouched',
    defaultValues: getFormDefaults(weekDays)
  });

  // Re-initialise form when the grant arrives (step mounts before group is created)
  React.useEffect(() => {
    reset(getFormDefaults(weekDays));
  }, [grantId]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (grantId) {
        await updateAccessGrantMutation({
          variables: {
            accessGrantId: grantId,
            input: { scheduleType: 'weekSchedule', weekSchedule: buildWeekSchedule(data) as WeekScheduleDayInput[] }
          }
        });
      }
      goToNext();
    } catch {
      // Error displayed via snackbar in onError above
    }
  });

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          key="group-schedule-step-back"
          id="group-schedule-back-button"
          variant="outlined"
          onClick={goToPrev}
          type="button"
        >
          {translation.back}
        </Button>
        <Button
          key="group-schedule-step-submit"
          id="group-schedule-next-button"
          variant="contained"
          type="submit"
          form={CREATE_GROUP_STEP_3_FORM_ID}
          loading={loading}
          endIcon={<ChevronRightIcon />}
        >
          {steps[STEP_INDEX + 1]?.label}
        </Button>
      </Box>
    );
  }, [goToPrev, loading, setFooterContent, steps, translation]);

  return (
    <Stack component="form" id={CREATE_GROUP_STEP_3_FORM_ID} onSubmit={onSubmit} noValidate>
      <WeekScheduleFields control={control} getValues={getValues} trigger={trigger} />
    </Stack>
  );
};

export default GroupScheduleStep;
